import { memo, useEffect, useMemo, useRef, useState } from 'react'

// ── Кеш пиков: url → Float32Array ───────────────────────────────
// Переиспользуется между ремоунтами и экземплярами плеера.
const peaksCache = new Map<string, Float32Array>()
const decodePromises = new Map<string, Promise<Float32Array | null>>()

// Количество столбиков в волне. Больше — детальнее структура (вокал/пауза/припев).
const BAR_COUNT = 200

/**
 * Скачивает аудио, декодирует через AudioContext и вычисляет RMS-пики по блокам.
 *
 * Почему RMS, а не peak:
 *   - Современная музыка после brickwall-мастеринга идёт с выровненными пиками
 *     (почти весь трек около 0 dBFS). Peak-метод делает волну плоской.
 *   - RMS (root mean square) — средняя энергия блока, коррелирует с субъективной
 *     громкостью. Тихие куплеты, громкие припевы, паузы видны по-разному.
 *   - Затем применяем dBFS → linear маппинг (log scale), чтобы визуал был
 *     «музыкальным»: тихое показано мягче, как это воспринимает ухо.
 *
 * Результат — массив из BAR_COUNT значений [0..1]. null если CORS/формат не дал.
 */
async function decodePeaks(url: string): Promise<Float32Array | null> {
	if (peaksCache.has(url)) return peaksCache.get(url)!
	const existing = decodePromises.get(url)
	if (existing) return existing

	const promise = (async () => {
		try {
			const res = await fetch(url, { mode: 'cors' })
			if (!res.ok) throw new Error(`HTTP ${res.status}`)
			const arrayBuffer = await res.arrayBuffer()
			const AudioCtx =
				window.AudioContext ||
				(window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
			if (!AudioCtx) throw new Error('no audio context')
			const ctx: AudioContext = new AudioCtx()
			const audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0))
			void ctx.close?.()

			// Берём первый канал — визуально результат идентичен стерео-миксу.
			const channel = audioBuffer.getChannelData(0)
			const blockSize = Math.max(1, Math.floor(channel.length / BAR_COUNT))
			const rawRms = new Float32Array(BAR_COUNT)
			let maxRms = 0

			for (let i = 0; i < BAR_COUNT; i++) {
				const start = i * blockSize
				const end = Math.min(channel.length, start + blockSize)
				let sumSq = 0
				const count = end - start
				for (let j = start; j < end; j++) {
					const v = channel[j]
					sumSq += v * v
				}
				const rms = count > 0 ? Math.sqrt(sumSq / count) : 0
				rawRms[i] = rms
				if (rms > maxRms) maxRms = rms
			}

			// ── Log-mapping через dBFS ──
			// dB = 20 * log10(rms). Диапазон типично -60..0.
			// Маппим [-60, 0] dB → [0, 1] linear. Тишина (rms≈0) → 0.
			// Маленькая подложка чтобы волна не пропадала полностью:
			//  — минимальная высота выставляется в рендер-цикле (Math.max(2, ...)).
			const peaks = new Float32Array(BAR_COUNT)
			const minDb = -60
			for (let i = 0; i < BAR_COUNT; i++) {
				const rms = rawRms[i]
				if (rms <= 0) {
					peaks[i] = 0
					continue
				}
				const db = 20 * Math.log10(rms)
				const clamped = Math.max(minDb, Math.min(0, db))
				peaks[i] = (clamped - minDb) / (0 - minDb) // 0..1
			}

			// Финальная «контрастность»: лёгкое усиление динамики.
			// Растягиваем диапазон используя 95-перцентиль вместо max,
			// чтобы один outlier не сжимал остальное.
			const sorted = Array.from(peaks).sort((a, b) => a - b)
			const p95 = sorted[Math.floor(sorted.length * 0.95)] || 1
			if (p95 > 0) {
				for (let i = 0; i < peaks.length; i++) {
					peaks[i] = Math.min(1, peaks[i] / p95)
				}
			}

			peaksCache.set(url, peaks)
			return peaks
		} catch {
			return null
		} finally {
			decodePromises.delete(url)
		}
	})()

	decodePromises.set(url, promise)
	return promise
}

/**
 * Fallback-пики — детерминированный псевдо-рандомный паттерн,
 * чтобы всегда что-то показать, даже если декод не удался.
 */
function fallbackPeaks(seed: string): Float32Array {
	let h = 0
	for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0
	const peaks = new Float32Array(BAR_COUNT)
	for (let i = 0; i < BAR_COUNT; i++) {
		// Псевдо-рандом Mulberry32
		h = (h + 0x6d2b79f5) | 0
		let t = h
		t = Math.imul(t ^ (t >>> 15), t | 1)
		t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
		const r = ((t ^ (t >>> 14)) >>> 0) / 4294967296
		// Форма «пульса»: центр выше краёв
		const shape = 0.55 + 0.45 * Math.sin((i / BAR_COUNT) * Math.PI)
		peaks[i] = Math.max(0.12, r * shape)
	}
	return peaks
}

interface WaveformProps {
	src: string
	progress: number // 0..1
	onSeek?: (ratio: number) => void
	color?: string
	progressColor?: string
	height?: number
	className?: string
	/** Позиция «ножниц» / cut marker (0..1). Рисуется вертикальной пунктирной
	 *  линией. Справа от неё столбики приглушаются — визуально показывает
	 *  «что будет заменено». Используется в extend-операции. */
	cutMarker?: number
	/** Цвет cut-маркера (по умолчанию amber). */
	cutMarkerColor?: string
}

export const Waveform = memo(function Waveform({
	src,
	progress,
	onSeek,
	color = 'rgba(255, 255, 255, 0.3)',
	progressColor = '#a78bfa',
	height: heightProp = 40,
	className,
	cutMarker,
	cutMarkerColor = 'rgba(251, 191, 36, 0.95)',
}: WaveformProps) {
	const canvasRef = useRef<HTMLCanvasElement>(null)
	const containerRef = useRef<HTMLDivElement>(null)
	// Fallback derived from src (no effect, no sync setState).
	const fallback = useMemo(() => fallbackPeaks(src), [src])
	// Decoded peaks — обновляется асинхронно после decode, привязаны
	// к конкретному src чтобы при смене URL fallback подхватывался сразу.
	const [decoded, setDecoded] = useState<{ src: string; peaks: Float32Array } | null>(null)
	const peaks = decoded?.src === src ? decoded.peaks : fallback
	// Динамические размеры по ResizeObserver — canvas адаптируется к CSS.
	// Это позволяет через CSS override (media query + !important) менять
	// высоту волны без пропов.
	const [size, setSize] = useState<{ width: number; height: number }>({
		width: 0,
		height: heightProp,
	})

	// Декод пиков асинхронно
	useEffect(() => {
		let cancelled = false
		decodePeaks(src).then((p) => {
			if (cancelled || !p) return
			setDecoded({ src, peaks: p })
		})
		return () => {
			cancelled = true
		}
	}, [src])

	// Отслеживание размеров контейнера (и width, и height)
	useEffect(() => {
		const el = containerRef.current
		if (!el) return
		const ro = new ResizeObserver(([entry]) => {
			const w = Math.round(entry.contentRect.width)
			const h = Math.round(entry.contentRect.height) || heightProp
			setSize((prev) => (prev.width === w && prev.height === h ? prev : { width: w, height: h }))
		})
		ro.observe(el)
		return () => ro.disconnect()
	}, [heightProp])

	// Отрисовка canvas
	useEffect(() => {
		const canvas = canvasRef.current
		const { width, height } = size
		if (!canvas || !peaks || width <= 0 || height <= 0) return
		const dpr = window.devicePixelRatio || 1
		canvas.width = width * dpr
		canvas.height = height * dpr
		const ctx = canvas.getContext('2d')
		if (!ctx) return
		ctx.scale(dpr, dpr)
		ctx.clearRect(0, 0, width, height)

		const barCount = peaks.length
		const gap = 1.5
		const barWidth = Math.max(1, (width - gap * (barCount - 1)) / barCount)
		const midY = height / 2
		const progressX = Math.round(width * Math.max(0, Math.min(1, progress)))
		// cutMarker — позиция «ножниц». Справа от неё столбики приглушаются.
		const hasCut = typeof cutMarker === 'number' && cutMarker > 0 && cutMarker < 1
		const cutX = hasCut ? Math.round(width * cutMarker!) : -1

		for (let i = 0; i < barCount; i++) {
			const peak = peaks[i]
			// Минимальная высота столбика — чтобы волна не схлопывалась в ноль
			const barHeight = Math.max(2, peak * (height - 4))
			const x = i * (barWidth + gap)
			const y = midY - barHeight / 2
			const isPlayed = x + barWidth / 2 < progressX
			const isAfterCut = hasCut && x + barWidth / 2 >= cutX
			// Столбики после cutMarker — приглушённо (будут заменены AI-генерацией).
			// Играющая часть до cut'а — progressColor, иначе — обычный color.
			let fillStyle: string
			if (isAfterCut) {
				fillStyle = 'rgba(255, 255, 255, 0.12)'
			} else if (isPlayed) {
				fillStyle = progressColor
			} else {
				fillStyle = color
			}
			ctx.fillStyle = fillStyle
			// Скруглённые столбики
			const r = Math.min(barWidth / 2, 1.5)
			if (ctx.roundRect) {
				ctx.beginPath()
				ctx.roundRect(x, y, barWidth, barHeight, r)
				ctx.fill()
			} else {
				ctx.fillRect(x, y, barWidth, barHeight)
			}
		}

		// Рисуем cut-маркер (amber пунктирная линия).
		if (hasCut) {
			ctx.save()
			ctx.strokeStyle = cutMarkerColor
			ctx.lineWidth = 2
			ctx.setLineDash([4, 3])
			ctx.beginPath()
			ctx.moveTo(cutX, 2)
			ctx.lineTo(cutX, height - 2)
			ctx.stroke()
			// Маленькая «шапочка» вверху — как метка ножниц
			ctx.setLineDash([])
			ctx.fillStyle = cutMarkerColor
			ctx.beginPath()
			ctx.arc(cutX, 4, 3, 0, Math.PI * 2)
			ctx.fill()
			ctx.restore()
		}
	}, [peaks, size, progress, color, progressColor, cutMarker, cutMarkerColor])

	const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (!onSeek) return
		const rect = e.currentTarget.getBoundingClientRect()
		const ratio = (e.clientX - rect.left) / rect.width
		onSeek(Math.max(0, Math.min(1, ratio)))
	}

	return (
		<div
			ref={containerRef}
			className={`media-player__waveform${className ? ' ' + className : ''}`}
			onClick={handleClick}
			style={{ height: heightProp }}
		>
			<canvas ref={canvasRef} style={{ width: '100%', height: '100%' }} />
		</div>
	)
})
